import type { ReactNode } from 'react';
import { classes } from '../outils/classes';
import { Lien } from '../outils/lien';
import styles from './Registre.module.css';

export type TonEtat = 'signer' | 'attente' | 'retard' | 'ok' | 'neutre';

export interface ProprietesPuceEtat {
  ton: TonEtat;
  /** Libellé obligatoire : l'état n'est jamais porté par la seule couleur. */
  libelle: string;
  className?: string;
}

export function PuceEtat({ ton, libelle, className }: ProprietesPuceEtat) {
  return <span className={classes(styles.puce, styles[ton], className)}>{libelle}</span>;
}

export interface ElementRegistre {
  id: string;
  reference: string;
  objet: ReactNode;
  sousTitre?: ReactNode;
  etat?: { ton: TonEtat; libelle: string };
  href?: string;
}

export interface ProprietesRegistre {
  titre?: ReactNode;
  /** Mention à droite de l'en-tête, par exemple « 9 entrées ». */
  compteur?: ReactNode;
  elements: ElementRegistre[];
  compacte?: boolean;
  niveau?: 2 | 3;
  className?: string;
  /** Contenu affiché quand la liste est vide. */
  vide?: ReactNode;
}

/** Registre : liste numérotée, référence en Newsreader, objet avec sous-titre, puce d'état. */
export function Registre({ titre, compteur, elements, compacte, niveau = 3, className, vide }: ProprietesRegistre) {
  const Titre = niveau === 2 ? 'h2' : 'h3';
  return (
    <section className={classes(styles.registre, compacte && styles.compacte, className)} aria-label={typeof titre === 'string' ? titre : undefined}>
      {titre || compteur ? (
        <header className={styles.entete}>
          {titre ? <Titre>{titre}</Titre> : <span />}
          {compteur ? <span className="cc-etiquette">{compteur}</span> : null}
        </header>
      ) : null}
      {elements.length === 0 && vide ? (
        vide
      ) : (
        <ol className={styles.liste}>
          {elements.map((e) => {
            const contenu = (
              <>
                <span className={styles.ref}>{e.reference}</span>
                <span className={styles.objet}>
                  {e.objet}
                  {e.sousTitre ? <small>{e.sousTitre}</small> : null}
                </span>
                {e.etat ? <PuceEtat ton={e.etat.ton} libelle={e.etat.libelle} /> : <span />}
              </>
            );
            return (
              <li key={e.id}>
                {e.href ? (
                  <Lien href={e.href} className={styles.ligne}>
                    {contenu}
                  </Lien>
                ) : (
                  <div className={styles.ligne}>{contenu}</div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
